import { Typography } from 'antd'
import { intersectionBy, keyBy } from 'lodash'
import React from 'react'

import { Department } from '@/modules/department'
import { PpObjective } from '@/schemas'

import { Node, defaultSize, drawPath, getTree, useMoveDom } from './okr-utils'

interface OKRMapProps {
  root: Department
  objectives: PpObjective[]
  departmentId: number
}

export const OKRMap: React.FC<OKRMapProps> = ({ root, objectives, departmentId }) => {
  if (!root) return null
  const ref = React.useRef<HTMLDivElement | null>(null)

  const { tree, nodes, objectivesMap } = React.useMemo(() => {
    const tree = getTree(root)
    return {
      tree,
      nodes: tree.descendants() as Node[],
      objectivesMap: keyBy(objectives, (o) => o.objectiveId),
    }
  }, [root])

  const [highlight, setHighlight] = React.useState({
    nodes: [] as Node[],
    objectives: [] as PpObjective[],
  })

  // 添加拖拽移动
  useMoveDom(ref)

  // 滚动到自己所在的部门
  React.useLayoutEffect(() => {
    if (ref.current) {
      const node = document.getElementById(`o-${departmentId}`)
      if (node) {
        node.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'center',
        })
      }
    }
  }, [ref.current, departmentId])

  React.useEffect(() => {
    setHighlight({ nodes: [], objectives: [] })
  }, [departmentId])

  const onClickObjective = (n: Node, o: PpObjective) => {
    const parentObjectiveIds: number[] = []

    let parentId: number | null = o.parentId
    while (parentId) {
      if (parentId in objectivesMap) {
        parentObjectiveIds.push(parentId)
        parentId = objectivesMap[parentId].parentId
      } else {
        parentId = null
      }
    }

    const highlightObjectives: PpObjective[] = [o]
    const highlightNodes: Node[] = [n]

    tree.each((node: Node) => {
      let found = false
      node.data.objectives.forEach((oo) => {
        if (parentObjectiveIds.includes(oo.objectiveId) || oo.parentId === o.objectiveId) {
          highlightObjectives.push(oo)
          found = true
        }
      })
      if (found) {
        highlightNodes.push(node)
      }
    })

    setHighlight({
      nodes: highlightNodes,
      objectives: highlightObjectives,
    })
  }

  const paths = nodes
    .filter((node: Node) => node.children)
    .map((node: Node) => {
      const d = drawPath(node)
      return (
        d && <path key={node.data.id} className='fill-none stroke-gray-400 stroke-1 text-stroke-opacity-40' d={d} />
      )
    })

  const highlightPaths = highlight.nodes
    .filter((node: Node) => node.children)
    .map((node: Node) => {
      const filterFn = (n: Node) =>
        intersectionBy(n.data.objectives, highlight.objectives, (o) => o.objectiveId).length > 0
      const d = drawPath(node, filterFn)
      return d && <path key={node.data.id} className='fill-none stroke-red-400 stroke-3 text-stroke-opacity-40' d={d} />
    })

  const cards = nodes.map((n: Node) => {
    const list = n.data.objectives.map((o) => (
      <li key={o.objectiveId} onClick={() => onClickObjective(n, o)} className='text-xs'>
        <Typography.Text
          ellipsis={{ tooltip: true }}
          type={highlight.objectives.find((oo) => oo.objectiveId === o.objectiveId) ? 'danger' : undefined}
        >
          {o.name}
        </Typography.Text>
      </li>
    ))
    return (
      <div
        key={n.data.id}
        id={`o-${n.data.id}`}
        className='bg-white border border-solid rounded border-gray-500 leading-tight py-4px top-0 left-0 absolute'
        style={{
          border: n.data.id === departmentId ? '2px solid #f97316' : undefined,
          width: defaultSize.node.width,
          transform: `translate(${n.x}px,${n.y}px)`,
        }}
      >
        <Typography.Text strong className='text-sm text-center block'>
          {n.data.name}
        </Typography.Text>
        <ul className='m-0 pr-12px pl-24px'>{list}</ul>
      </div>
    )
  })

  return (
    <div ref={ref} className='h-screen w-full relative overflow-auto select-none'>
      <svg
        width={tree.extents.right - tree.extents.left + defaultSize.margin.left + defaultSize.margin.right}
        height={tree.extents.bottom + defaultSize.margin.top + defaultSize.margin.bottom}
      >
        <g transform={`translate(${defaultSize.margin.left - tree.extents.left},${defaultSize.margin.top})`}>
          {paths}
          {highlightPaths}
        </g>
      </svg>
      <div
        className='top-0 left-0 absolute'
        style={{
          transform: `translate(${defaultSize.margin.left - tree.extents.left}px,${defaultSize.margin.top}px)`,
        }}
      >
        {cards}
      </div>
    </div>
  )
}
